/*import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import { Water } from 'three/examples/jsm/objects/Water.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
*/

import * as THREE from 'https://esm.run/three';
import { OrbitControls } from 'https://esm.run/three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from "https://esm.run/three/examples/jsm/loaders/GLTFLoader.js";
import { FontLoader, TextGeometry } from 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.module.js';
import { RGBELoader } from 'https://esm.run/three/examples/jsm/loaders/RGBELoader.js'
import { Water } from 'https://esm.run/three/examples/jsm/objects/Water.js';
import { gsap } from 'https://cdn.jsdelivr.net/npm/gsap@3.10.4/index.js';


const vertexShader = `
uniform mat4 textureMatrix;
            uniform float time;

            varying vec4 mirrorCoord;
            varying vec4 worldPosition;

            #include <common>
            #include <fog_pars_vertex>
            #include <shadowmap_pars_vertex>
            #include <logdepthbuf_pars_vertex>

            uniform vec4 waveA;
            uniform vec4 waveB;
            uniform vec4 waveC;

            vec3 GerstnerWave (vec4 wave, vec3 p) {
            	float steepness = wave.z;
            	float wavelength = wave.w;
            	float k = 2.0 * PI / wavelength;
            	float c = sqrt(9.8 / k);
            	vec2 d = normalize(wave.xy);
            	float f = k * (dot(d, p.xy) - c * time);
            	float a = steepness / k;

            	return vec3(
            		d.x * (a * cos(f)),
            		d.y * (a * cos(f)),
            		a * sin(f)
            	);
            }

            void main() {
            	mirrorCoord = modelMatrix * vec4( position, 1.0 );
            	worldPosition = mirrorCoord.xyzw;
            	mirrorCoord = textureMatrix * mirrorCoord;

            	vec3 p = position.xyz;
            	p += GerstnerWave(waveA, position.xyz);
            	p += GerstnerWave(waveB, position.xyz);
            	p += GerstnerWave(waveC, position.xyz);
            	gl_Position = projectionMatrix * modelViewMatrix * vec4( p.x, p.y, p.z, 1.0);

            	#include <beginnormal_vertex>
            	#include <defaultnormal_vertex>
            	#include <logdepthbuf_vertex>
            	#include <fog_vertex>
            	#include <shadowmap_vertex>
            }
`
const fragmentShader = `
uniform sampler2D mirrorSampler;
            uniform float alpha;
            uniform float time;
            uniform float size;
            uniform float distortionScale;
            uniform sampler2D normalSampler;
            uniform vec3 sunColor;
            uniform vec3 sunDirection;
            uniform vec3 eye;
            uniform vec3 waterColor;

            varying vec4 mirrorCoord;
            varying vec4 worldPosition;

            vec4 getNoise( vec2 uv ) {
                vec2 uv0 = ( uv / 103.0 ) + vec2(time / 17.0, time / 29.0);
                vec2 uv1 = uv / 107.0-vec2( time / -19.0, time / 31.0 );
                vec2 uv2 = uv / vec2( 8907.0, 9803.0 ) + vec2( time / 101.0, time / 97.0 );
                vec2 uv3 = uv / vec2( 1091.0, 1027.0 ) - vec2( time / 109.0, time / -113.0 );
                vec4 noise = texture2D( normalSampler, uv0 ) +
                    texture2D( normalSampler, uv1 ) +
                    texture2D( normalSampler, uv2 ) +
                    texture2D( normalSampler, uv3 );
                return noise * 0.5 - 1.0;
            }

            void sunLight( const vec3 surfaceNormal, const vec3 eyeDirection, float shiny, float spec, float diffuse, inout vec3 diffuseColor, inout vec3 specularColor ) {
                vec3 reflection = normalize( reflect( -sunDirection, surfaceNormal ) );
                float direction = max( 0.0, dot( eyeDirection, reflection ) );
                specularColor += pow( direction, shiny ) * sunColor * spec;
                diffuseColor += max( dot( sunDirection, surfaceNormal ), 0.0 ) * sunColor * diffuse;
            }

            #include <common>
            #include <packing>
            #include <bsdfs>
            #include <fog_pars_fragment>
            #include <logdepthbuf_pars_fragment>
            #include <lights_pars_begin>
            #include <shadowmap_pars_fragment>
            #include <shadowmask_pars_fragment>

            void main() {

                #include <logdepthbuf_fragment>
                vec4 noise = getNoise( worldPosition.xz * size );
                vec3 surfaceNormal = normalize( noise.xzy * vec3( 1.5, 1.0, 1.5 ) );

                vec3 diffuseLight = vec3(0.0);
                vec3 specularLight = vec3(0.0);

                vec3 worldToEye = eye-worldPosition.xyz;
                vec3 eyeDirection = normalize( worldToEye );
                sunLight( surfaceNormal, eyeDirection, 100.0, 2.0, 0.5, diffuseLight, specularLight );

                float distance = length(worldToEye);

                vec2 distortion = surfaceNormal.xz * ( 0.001 + 1.0 / distance ) * distortionScale;
                vec3 reflectionSample = vec3( texture2D( mirrorSampler, mirrorCoord.xy / mirrorCoord.w + distortion ) );

                float theta = max( dot( eyeDirection, surfaceNormal ), 0.0 );
                float rf0 = 0.3;
                float reflectance = rf0 + ( 1.0 - rf0 ) * pow( ( 1.0 - theta ), 5.0 );
                vec3 scatter = max( 0.0, dot( surfaceNormal, eyeDirection ) ) * waterColor;
                vec3 albedo = mix( ( sunColor * diffuseLight * 0.3 + scatter ) * getShadowMask(), ( vec3( 0.1 ) + reflectionSample * 0.9 + reflectionSample * specularLight ), reflectance);
                vec3 outgoingLight = albedo;
                gl_FragColor = vec4( outgoingLight, alpha );

                #include <tonemapping_fragment>
                #include <fog_fragment>
            }
`

function initThreeJsDemo() {

    /**
     * Loaders
     */

    const loadingBarElement = document.querySelector('.loading-bar')
    const loadingManager = new THREE.LoadingManager(
        // Loaded
        () =>
        {
            // Wait a little
            window.setTimeout(() =>
            {
                // Animate overlay
                gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 3, value: 0, delay: 1 })
    
                // Update loadingBarElement
                loadingBarElement.classList.add('ended')
                loadingBarElement.style.transform = ''
            }, 500)
        },
    
        // Progress
        (itemUrl, itemsLoaded, itemsTotal) =>
        {
            // Calculate the progress and update the loadingBarElement
            const progressRatio = itemsLoaded / itemsTotal
            loadingBarElement.style.transform = `scaleX(${progressRatio})`
        }
    )
    const gltfLoader = new GLTFLoader(loadingManager)
    const rgbeLoader = new RGBELoader(loadingManager)
    
    /**
     * Base
     */
    // Debug
    const global = {}
    
    let objective_camera = new THREE.Vector3(0,9,17);
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2()
    
    // Canvas
    const canvas = document.querySelector('canvas.webgl')
    
    // Scene
    const scene = new THREE.Scene()
    
    /**
     * Update all materials
     */
    const updateAllMaterials = () =>
    {
        scene.traverse((child) =>
        {
            if(child.isMesh && child.material.isMeshStandardMaterial)
            {
                child.material.envMapIntensity = global.envMapIntensity
    
                child.castShadow = true
                child.receiveShadow = true
            }
        })
    }

/**
 * Overlay
 */

const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1)
const overlayMaterial = new THREE.ShaderMaterial({
    // wireframe: true,
    transparent: true,
    uniforms:
    {
        uAlpha: { value: 1 }
    },
    vertexShader: `
        void main()
        {
            gl_Position = vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform float uAlpha;

        void main()
        {
            gl_FragColor = vec4(0,0,0, uAlpha);
        }
    `
})
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
scene.add(overlay)
  
    
    /**
     * Environment map
     */
    
    // Global intensity
    global.envMapIntensity = 1
    
    
    // HDR (RGBE) equirectangular
    rgbeLoader.load('https://raw.githubusercontent.com/alon2138/hosting-atborder/main/sky2.hdr', (environmentMap) =>
    {
        environmentMap.mapping = THREE.EquirectangularReflectionMapping
    
        scene.background = environmentMap
        scene.environment = environmentMap
    })
    
    
    /**
     * Directional light
     */
    
    const directionalLight = new THREE.DirectionalLight('#ffffff', 2)
    directionalLight.position.set(- 4, 6.5, 2.5)
    scene.add(directionalLight)
    
    // Shadows
    directionalLight.castShadow = true
    directionalLight.shadow.camera.far = 15
    directionalLight.shadow.normalBias = 0.027
    directionalLight.shadow.bias = - 0.004
    directionalLight.shadow.mapSize.set(512, 512)
    
    // Target
    directionalLight.target.position.set(0, 4, 0)
    directionalLight.target.updateWorldMatrix()
    
    /**
     * Models
     */
    const textureLoader = new THREE.TextureLoader()
    const matcapTexture = textureLoader.load('https://raw.githubusercontent.com/alon2138/hosting-atborder/main/2.png')
    matcapTexture.encoding = THREE.sRGBEncoding;
    
    let container, stats;
    let water;
    let delta;
    let texts = [];
    let boxes = [];
    let responsiveObjects = [];
    let vertical = false;
    let arouse;

    let model,shark, boat;
    let modelReady = false;
    let sharkReady = false;
    
    gltfLoader.load(
        'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/arouse.glb',
        (gltf) =>
        {
            model = gltf.scene;
            model.scale.set(4, 4, 4)
            model.position.set(0, 6, -10)
            model.material = new THREE.MeshMatcapMaterial({ matcap: matcapTexture })
            scene.add(model)
            responsiveObjects.push(model)

            updateAllMaterials()
        }
    );
    
    let mixer;
    gltfLoader.load(
        'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/shark.glb',
        (gltf) =>
        {
            shark = gltf.scene;
            shark.position.set(-14,-6,-1)
            shark.scale.set(3,3,3)
            scene.add(shark)
            responsiveObjects.push(shark);

            mixer = new THREE.AnimationMixer( shark );
            const clips = gltf.animations;
            const clip = THREE.AnimationClip.findByName(clips, "CubeAction.001")
            const action = mixer.clipAction( clip )
            action.play()
    
    
            updateAllMaterials()
        }
    );
    
    /**
     * Waves animation
     */
    
    const waves = {
        A: { direction: 130, steepness: 0.1, wavelength: 60 },
        B: { direction: 30, steepness: 0.2, wavelength: 34 },
        C: { direction: 60, steepness: 0.4, wavelength: 15 },
    };
    
    function getWaveInfo( x, z, time ) {
    
        const pos = new THREE.Vector3();
        const tangent = new THREE.Vector3( 1, 0, 0 );
        const binormal = new THREE.Vector3( 0, 0, 1 );
        Object.keys( waves ).forEach( ( wave ) => {
    
            const w = waves[ wave ];
            const k = ( Math.PI * 2 ) / w.wavelength;
            const c = Math.sqrt( 9.8 / k );
            const d = new THREE.Vector2(
                Math.sin( ( w.direction * Math.PI ) / 180 ),
                - Math.cos( ( w.direction * Math.PI ) / 180 )
            );
            const f = k * ( d.dot( new THREE.Vector2( x, z ) ) - c * time );
            const a = w.steepness / k;
    
            pos.x += d.y * ( a * Math.cos( f ) );
            pos.y += a * Math.sin( f );
            pos.z += d.x * ( a * Math.cos( f ) );
    
            tangent.x += - d.x * d.x * ( w.steepness * Math.sin( f ) );
            tangent.y += d.x * ( w.steepness * Math.cos( f ) );
            tangent.z += - d.x * d.y * ( w.steepness * Math.sin( f ) );
    
            binormal.x += - d.x * d.y * ( w.steepness * Math.sin( f ) );
            binormal.y += d.y * ( w.steepness * Math.cos( f ) );
            binormal.z += - d.y * d.y * ( w.steepness * Math.sin( f ) );
    
        } );
    
        const normal = binormal.cross( tangent ).normalize();
    
        return { position: pos, normal: normal };
    
    }
    
    function updateBoxes( delta ) {
    
        const t = water.material.uniforms[ 'time' ].value;
        boxes.forEach( ( b ) => {
    
            const waveInfo = getWaveInfo( b.position.x, b.position.z, t );
            let positionObjective = new THREE.Vector3( b.position.x, waveInfo.position.y - 3.4, b.position.z)
    
            if(b==crate && crateHovered){ 
                positionObjective.y += 2.5;
            }else if(!crateHovered){
                const quat = new THREE.Quaternion().setFromEuler(
                    new THREE.Euler( waveInfo.normal.x, waveInfo.normal.y, waveInfo.normal.z )
                );
                b.quaternion.rotateTowards( quat, delta * 0.5 );    
            }
            
            if(b==bottle && bottleHovered){ 
                positionObjective.y += 2.5;
            }else if(!bottleHovered){
                const quat = new THREE.Quaternion().setFromEuler(
                    new THREE.Euler( waveInfo.normal.x, waveInfo.normal.y, waveInfo.normal.z )
                );
                b.quaternion.rotateTowards( quat, delta * 0.5 );    
            }
            
            
            if(b==AboutUs && AboutUsHovered){ 
                positionObjective.y += 2;
            }else if(!AboutUsHovered){
                const quat = new THREE.Quaternion().setFromEuler(
                    new THREE.Euler( waveInfo.normal.x, waveInfo.normal.y, waveInfo.normal.z )
                );
                b.quaternion.rotateTowards( quat, delta * 0.5 );    
            }
    
            for (let i = 0; i < texts.length; i++) {
                if(texts[i]==b){
                    positionObjective.y += 1
                    texts[i].lookAt(0, 7, 19)
                } 
            }
    
            b.position.lerp(new THREE.Vector3(b.position.x, positionObjective.y ,b.position.z),0.01)
            
        } );
    
    }
    
    
    
    /**
     * Fonts
     */
    const fontLoader = new FontLoader()
    
    let bottle;
    let bottleShpere;
    let bottleHovered = false;
    //bottle
    gltfLoader.load(
        'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/bottle.glb',
        (gltf) =>
        {
            fontLoader.load(
                'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/SEVEN SEAS_Regular.json',
                (font) =>
                {
                    // Material
                    const material = new THREE.MeshMatcapMaterial({ matcap: matcapTexture })
            
                    // Text
                    const textGeometry = new TextGeometry(
                        'CONTACT',
                        {
                            font: font,
                            size: 1.6,
                            height: 0.1,
                            curveSegments: 12,
                            bevelEnabled: true,
                            bevelThickness: 0.1,
                            bevelSize: 0.02,
                            bevelOffset: 0,
                            bevelSegments: 5
                        }
                    )
                    textGeometry.center()
            
                    const text = new THREE.Mesh(textGeometry, material)
                    scene.add(text)
    
                    text.position.set(-15, 0, -6)
    
                    texts[0] = text
                    boxes.push(text)
                    responsiveObjects.push(text);
    
                }
            )
    
            bottle = gltf.scene;
            bottle.position.set( -16,0,-9);
            bottle.scale.set( 22, 22, 22);
    
            scene.add(bottle);
            boxes.push(bottle)
            responsiveObjects.push(bottle);
    
            bottleShpere = new THREE.Mesh( new THREE.SphereGeometry( 5.5, 5.5, 5.5 ) , new THREE.MeshBasicMaterial( {  } ) );
            bottleShpere.position.set( -16,-1,-9);
            scene.add( bottleShpere );
            responsiveObjects.push(bottleShpere);
            bottleShpere.visible= false
    
            updateAllMaterials()
        }
    );
    
    let crate;
    let crateShpere;
    let crateHovered = false;
    //Crate
    gltfLoader.load(
        'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/crate.glb',
        (gltf) =>
        {
            fontLoader.load(
                'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/SEVEN SEAS_Regular.json',
                (font) =>
                {
                    // Material
                    const material = new THREE.MeshMatcapMaterial({ matcap: matcapTexture })
            
                    // Text
                    const textGeometry = new TextGeometry(
                        'SHOP',
                        {
                            font: font,
                            size: 1.6,
                            height: 0.1,
                            curveSegments: 12,
                            bevelEnabled: true,
                            bevelThickness: 0.1,
                            bevelSize: 0.02,
                            bevelOffset: 0,
                            bevelSegments: 5
                        }
                    )
                    textGeometry.center()
            
                    const text = new THREE.Mesh(textGeometry, material)
                    scene.add(text)
    
                    text.position.set( 3, 0 , 0)
                    
                    texts[1] = text
                    boxes.push(text)
                    responsiveObjects.push(text);
                }
            )
    
            crate = gltf.scene;
            crate.position.set( 3, 0, -5);
            crate.scale.set( 1, 1,1);
    
            scene.add(crate);
            boxes.push(crate);
            responsiveObjects.push(crate);
    
            crateShpere = new THREE.Mesh( new THREE.SphereGeometry( 5.5, 5.5, 5.5 ) , new THREE.MeshBasicMaterial( {} ) );
            crateShpere.position.set( 3, -1 ,-5);
            scene.add( crateShpere );
            responsiveObjects.push(crateShpere);
            crateShpere.visible= false
    
            updateAllMaterials()
        }
    );
    
    let AboutUs;
    let AboutUsShpere;
    let AboutUsHovered = false;
    //AboutUs
    gltfLoader.load(
        'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/floaty.glb',
        (gltf) =>
        {
            fontLoader.load(
                'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/SEVEN SEAS_Regular.json',
                (font) =>
                {
                    // Material
                    const material = new THREE.MeshMatcapMaterial({ matcap: matcapTexture })
            
                    // Text
                    const textGeometry = new TextGeometry(
                        'About us',
                        {
                            font: font,
                            size: 1.6,
                            height: 0.1,
                            curveSegments: 12,
                            bevelEnabled: true,
                            bevelThickness: 0.1,
                            bevelSize: 0.02,
                            bevelOffset: 0,
                            bevelSegments: 5
                        }
                    )
                    textGeometry.center()
            
                    const text = new THREE.Mesh(textGeometry, material)
                    scene.add(text)
    
                    text.position.set( 18, 0 , 1)
    
                    texts[2] = text
                    boxes.push(text)
                    responsiveObjects.push(text);
                }
            )
    
            AboutUs = gltf.scene;
            AboutUs.position.set( 21,0,-2);
            AboutUs.scale.set( 1, 1,1);
    
            scene.add(AboutUs);
            boxes.push(AboutUs)
            responsiveObjects.push(AboutUs);
    
            AboutUsShpere = new THREE.Mesh( new THREE.SphereGeometry( 5.5, 5.5, 5.5 ) , new THREE.MeshBasicMaterial( {} ) );
            AboutUsShpere.position.set( 21,-1,-2);
            scene.add( AboutUsShpere );
            responsiveObjects.push(AboutUsShpere);
            AboutUsShpere.visible= false
    
            updateAllMaterials()
        }
    );
    
    // Water
    
    
    const waterGeometry = new THREE.PlaneGeometry( 2048, 2048, 512, 512 );
    
    water = new Water( waterGeometry, {
    
        textureWidth: 512,
        textureHeight: 512,
        waterNormals: new THREE.TextureLoader().load(
            'https://raw.githubusercontent.com/alon2138/hosting-atborder/main/waternormals.jpg',
            function ( texture ) {
    
                texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    
            }
        ),
        sunDirection: new THREE.Vector3(),
        sunColor: 0xffffff,
        waterColor: 0x001e0f,
        distortionScale: 2.5,
        fog: scene.fog !== undefined,
    } );
    
    water.rotation.x = - Math.PI / 2;
    
    water.material.onBeforeCompile = function ( shader ) {
    
        shader.uniforms.waveA = {
            value: [
                Math.sin( ( waves.A.direction * Math.PI ) / 180 ),
                Math.cos( ( waves.A.direction * Math.PI ) / 180 ),
                waves.A.steepness,
                waves.A.wavelength,
            ],
        };
        shader.uniforms.waveB = {
            value: [
                Math.sin( ( waves.B.direction * Math.PI ) / 180 ),
                Math.cos( ( waves.B.direction * Math.PI ) / 180 ),
                waves.B.steepness,
                waves.B.wavelength,
            ],
        };
        shader.uniforms.waveC = {
            value: [
                Math.sin( ( waves.C.direction * Math.PI ) / 180 ),
                Math.cos( ( waves.C.direction * Math.PI ) / 180 ),
                waves.C.steepness,
                waves.C.wavelength,
            ],
        };
    
        shader.vertexShader = vertexShader
        shader.fragmentShader = fragmentShader
    
    
    };
        
    water.position.y =-4
    scene.add( water );
    
    // Events
    function onMouseMove( event ) {
        objective_camera.set((( event.clientX / window.innerWidth ) * 2) - 1,- ( event.clientY / window.innerHeight ) * 2 + 7, 17)
        
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      
        raycaster.setFromCamera(mouse, camera);
      
        var intersects;
    
        if(crate){
            intersects = raycaster.intersectObject(crateShpere, true);
    
            if (intersects.length > 0) {
                  crateHovered = true;
            }else{
                crateHovered = false;
            }
        } 
    
        if(bottle){
            intersects = raycaster.intersectObject(bottleShpere, true);
    
            if (intersects.length > 0) {
                bottleHovered = true;
            }else{
                bottleHovered = false;
            }
        }
    
        if(AboutUs){
            intersects = raycaster.intersectObject(AboutUsShpere, true);
    
            if (intersects.length > 0) {
                AboutUsHovered = true;
            }else{
                AboutUsHovered = false;
            }
        }
    }
    
    function onclick(event) {
        if(bottleHovered)window.location.href = "/pages/contact";
        if(crateHovered)window.location.href = "/collections/all";
        if(AboutUsHovered)window.location.href = "/pages/about-us";
    }
    
    const sizes = {
        width: window.innerWidth,
        height: window.innerHeight
    }
    
    const oldSizes = {
        width: 1,
        height: 1
    }

    function onResize() {
        //Update oldSizes
        oldSizes.width=sizes.width
        oldSizes.height=sizes.width

        // Update sizes
        sizes.width = window.innerWidth
        sizes.height = window.innerHeight
    
        // Update camera
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()
    
        // Update renderer
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        if(sizes.height > sizes.width){
            vertical = true
        }else{
            vertical = false
        }

    }

    window.addEventListener( 'click', onclick, false );
    window.addEventListener( 'mousemove', onMouseMove, false );
    window.addEventListener( 'resize', onResize, false );
    
    /**
     * Camera
     */
    // Base camera
    const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 1000)
    camera.position.set(0,100,190)
    
    scene.add(camera)
    
    // Controls
    const controls = new OrbitControls(camera, canvas)
    controls.target.y = 3.5
    controls.enableDamping = true
    
    controls.enabled = true
    controls.enablePan = true;

    /**
     * Renderer
     */
    const renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        antialias: true
    })
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // Tone mapping
    renderer.toneMapping = THREE.ReinhardToneMapping
    renderer.toneMappingExposure = 3
    
    // Physically accurate lighting
    renderer.useLegacyLights = false
    
    // Shadows
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap
    
    /**
     * Layout
     */


    /**
     * Animate
     */
    const clock = new THREE.Clock()
    let initializedMeshes = true;
    let shaderupdated = false;
    const tick = () =>
    {
        /**
         * Update meshes position
         */

        if(texts[2]){
            if(initializedMeshes){
                /**
                 * HORIZONTAL
                 */
                bottle.userData.Desktop = new THREE.Vector3(-16,0,-9)
                bottle.userData.ScaleDesktop = new THREE.Vector3(22,22,22)
                bottleShpere.userData.Desktop = new THREE.Vector3(-16,-1,-9)
                bottleShpere.userData.ScaleDesktop = new THREE.Vector3(1,1,1)
                texts[0].userData.Desktop = new THREE.Vector3(-15, 0, -6)
                texts[0].userData.ScaleDesktop = new THREE.Vector3(1,1,1)
                
                crate.userData.Desktop = new THREE.Vector3(3,0,-5)
                crate.userData.ScaleDesktop = new THREE.Vector3(1,1,1)
                crateShpere.userData.Desktop = new THREE.Vector3(3, -1 ,-5)
                crateShpere.userData.ScaleDesktop = new THREE.Vector3(1,1,1)
                texts[1].userData.Desktop = new THREE.Vector3(3, 0 , 0)
                texts[1].userData.ScaleDesktop = new THREE.Vector3(1,1,1)
                
                AboutUs.userData.Desktop = new THREE.Vector3(18,0,-2)
                AboutUs.userData.ScaleDesktop = new THREE.Vector3(1,1,1)
                AboutUsShpere.userData.Desktop = new THREE.Vector3(21,-1,-2)
                AboutUsShpere.userData.ScaleDesktop = new THREE.Vector3(1,1,1)
                texts[2].userData.Desktop = new THREE.Vector3(16, 0 , 1)
                texts[2].userData.ScaleDesktop = new THREE.Vector3(0.8,0.8,0.8)

                shark.userData.Desktop = new THREE.Vector3(3,3,3)
                shark.userData.ScaleDesktop = new THREE.Vector3(1,1,1)

                model.userData.Desktop = new THREE.Vector3(0, 6, -10)
                model.userData.ScaleDesktop = new THREE.Vector3(4,4,4)


                /**
                 * VERTICAL
                 */
                bottle.userData.Mobile = new THREE.Vector3(-3.5,1,-0.5)
                bottle.userData.ScaleMobile = new THREE.Vector3(17,17,17)
                bottleShpere.userData.Mobile = new THREE.Vector3(-3.5,1,-0.5)            ,bottleShpere.position.y = -3.5
                bottleShpere.userData.ScaleMobile = new THREE.Vector3(0.6,0.6,0.6)
                texts[0].userData.Mobile = new THREE.Vector3(-3.5, 0, 1)
                texts[0].userData.ScaleMobile = new THREE.Vector3(0.6,0.6,0.6)
                
                crate.userData.Mobile = new THREE.Vector3(-1,1,-13)
                crate.userData.ScaleMobile = new THREE.Vector3(0.8,0.8,0.8)
                crateShpere.userData.Mobile = new THREE.Vector3(-1, 1 ,-13)            ,crateShpere.position.y = -3.5
                crateShpere.userData.ScaleMobile = new THREE.Vector3(0.6,0.6,0.6)
                texts[1].userData.Mobile = new THREE.Vector3(-1,1,-8)
                texts[1].userData.ScaleMobile = new THREE.Vector3(1,1,1)
                
                AboutUs.userData.Mobile = new THREE.Vector3(5,1,-3)
                AboutUs.userData.ScaleMobile = new THREE.Vector3(1,1,1)
                AboutUsShpere.userData.Mobile = new THREE.Vector3(5,-1,-3)            ,AboutUsShpere.position.y = -3.5
                AboutUsShpere.userData.ScaleMobile = new THREE.Vector3(0.6,0.6,0.6)
                texts[2].userData.Mobile = new THREE.Vector3(4.5, 1, -1)
                texts[2].userData.ScaleMobile = new THREE.Vector3(0.6,0.6,0.6)

                shark.userData.Mobile = new THREE.Vector3(3,30,3)
                shark.userData.ScaleMobile = new THREE.Vector3(1,1,1)

                model.userData.Mobile = new THREE.Vector3(0, 0, -13)
                model.userData.ScaleMobile = new THREE.Vector3(3,3,3)

                initializedMeshes=false
            }

            if(vertical){
                responsiveObjects.forEach(function(object) {
                    object.position.x=object.userData.Mobile.x
                    object.position.z=object.userData.Mobile.z
                    
                    object.scale.x=object.userData.ScaleMobile.x
                    object.scale.y=object.userData.ScaleMobile.y
                    object.scale.z=object.userData.ScaleMobile.z
                });
            }else{
                responsiveObjects.forEach(function(object) {
                    object.position.x=object.userData.Desktop.x
                    object.position.z=object.userData.Desktop.z

                    object.scale.x=object.userData.ScaleDesktop.x
                    object.scale.y=object.userData.ScaleDesktop.y
                    object.scale.z=object.userData.ScaleDesktop.z
                });
            }
        }
        
        delta = clock.getDelta();

        //update shader
        if(vertexShader && fragmentShader && !shaderupdated){
              water.material.onBeforeCompile = function ( shader ) {
            
                shader.uniforms.waveA = {
                    value: [
                        Math.sin( ( waves.A.direction * Math.PI ) / 180 ),
                        Math.cos( ( waves.A.direction * Math.PI ) / 180 ),
                        waves.A.steepness,
                        waves.A.wavelength,
                    ],
                };
                shader.uniforms.waveB = {
                    value: [
                        Math.sin( ( waves.B.direction * Math.PI ) / 180 ),
                        Math.cos( ( waves.B.direction * Math.PI ) / 180 ),
                        waves.B.steepness,
                        waves.B.wavelength,
                    ],
                };
                shader.uniforms.waveC = {
                    value: [
                        Math.sin( ( waves.C.direction * Math.PI ) / 180 ),
                        Math.cos( ( waves.C.direction * Math.PI ) / 180 ),
                        waves.C.steepness,
                        waves.C.wavelength,
                    ],
                };
            
                shader.vertexShader = vertexShader
                shader.fragmentShader = fragmentShader
            
            
            };
          
          shaderupdated=true
        }
    

/*
        if(sizes.height > oldSizes.width) {
            //Vertical
            bottle.position.set(bottleMobile)
            bottleShpere.position.set(bottleMobile)
            texts[0].position.set(bottleMobile)
        
            crate.position.set(crateMobile)
            crateShpere.position.set(crateMobile)
            texts[1].position.set(crateMobile)

            AboutUs.position.set(AboutUsMobile)
            AboutUsShpere.position.set(AboutUsMobile)
            texts[2].position.set(AboutUsMobile)


        }else{  
            //Horizontal
            bottle.position.set(bottleDesktop)
            bottleShpere.position.set(bottleDesktop)
            texts[0].position.set(bottleDesktop)
            
            crate.position.set(crateDesktop)
            crateShpere.position.set(crateDesktop)
            texts[1].position.set(crateDesktop)
            
            AboutUs.position.set(AboutUsDesktop)
            AboutUsShpere.position.set(AboutUsDesktop)
            texts[2].position.set(AboutUsDesktop)

        }*/

        if(mixer)mixer.update( delta );    
    
        if(crateHovered || bottleHovered || AboutUsHovered){
            document.body.style.cursor = 'pointer';
        }else{
            document.body.style.cursor = 'default';
        }
    
        //Update camera position
        camera.position.lerp(objective_camera ,0.02)

    
        water.material.uniforms[ 'time' ].value += 0.0015;
        updateBoxes( delta );
    
        // Update controls
        controls.update()
    
        // Render
        renderer.render(scene, camera)
    
        // Call tick again on the next frame
        window.requestAnimationFrame(tick)
    

    }
    
    tick()
    onResize()
    }
    initThreeJsDemo();